import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import { Anchor } from '@/components/atoms/anchor';
import { Checkbox } from '@/components/atoms/checkbox';
import { StyledInput } from '@/components/atoms/inputs/styled';
import { LoadingButton } from '@/components/atoms/Button/loading';
import { AuthTemplate } from '@/components/template/auth';
import { AppRouters } from '@/utils/constants/routers';
import { FormContainer, SubField } from './styles';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { PublicRoute } from '@/components/organisms/route/public';
import { EmailOutlined, PasswordOutlined } from '@mui/icons-material';
import { useAlert } from '@/hooks/useAlert';
import { Alert } from '@/components/molecules/alert';
import { Skeleton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '@/hooks/useAuth';
import { LoginUser, LoginValues } from '@/graphql/types/user';
import { MUTATION_LOGIN_USER } from '@/graphql/mutation/user';
import { useApolloClient } from '@apollo/client';
import { REMEMBER_ME_KEY } from '@/context/auth';
import { useTranslation } from '@/hooks/useTranslation';
import { useLanguage } from '@/hooks/useLanguage';

export default function LoginPage() {
  const apolloClient = useApolloClient();
  const navigate = useNavigate();
  const theme = useTheme();

  const { t } = useTranslation();
  const { loading: languageLoading } = useLanguage();

  const [loading, setLoading] = useState(false);

  const { alert, showAlert, handleCloseAlert } = useAlert();
  const { getUser, getToken } = useAuth();

  const validationSchema = Yup.object().shape({
    email: Yup.string().email().required(t('messages.error.error.email_required')),
    password: Yup.string().required(t('messages.error.error.password_required')),
  });

  const initialValues = {
    email: '',
    password: '',
    rememberMe: false,
  };

  const loginUser = async (values: LoginValues) => {
    const { data } = await apolloClient.mutate<{ loginUser: LoginUser }>({
      mutation: MUTATION_LOGIN_USER,
      variables: {
        input: {
          email: values.email,
          password: values.password,
        },
      },
    });

    if (data) {
      getUser(data.loginUser.user);
      getToken(data.loginUser.access_token);
    }
  };

  const handleSubmit = async (values: typeof initialValues) => {
    try {
      setLoading(true);
      localStorage.setItem(REMEMBER_ME_KEY, String(values.rememberMe));
      await loginUser({ email: values.email, password: values.password });
      navigate(AppRouters.home);
    } catch (error) {
      showAlert('error', String(error).split('Error: ')[1]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = 'KYT-e | GBF - Login';
  }, []);

  return (
    <PublicRoute>
      <AuthTemplate
        title={t('page.login.login.title')}
        subtitle={t('page.login.login.description')}
        anchorText={t('label.label.login.no_account')}
        languageLoading={languageLoading}
      >
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
        >
          {({ values, errors, handleChange }) => (
            <Form>
              <FormContainer>
                <StyledInput
                  name="email"
                  label={
                    languageLoading ? (
                      <Skeleton
                        variant="text"
                        width={100}
                        height={20}
                        sx={{ transform: 'none' }}
                        animation="wave"
                      />
                    ) : (
                      <>{t('label.label.email')}</>
                    )
                  }
                  margin="none"
                  type="email"
                  startIcon={<EmailOutlined color="action" />}
                  onChange={handleChange}
                  helperText={errors.email}
                  error={Boolean(errors.email)}
                />

                <FormContainer gap={2}>
                  <StyledInput
                    name="password"
                    label={
                      languageLoading ? (
                        <Skeleton
                          variant="text"
                          width={100}
                          height={20}
                          sx={{ transform: 'none' }}
                          animation="wave"
                        />
                      ) : (
                        <>{t('label.label.login.password')}</>
                      )
                    }
                    margin="none"
                    type="password"
                    startIcon={<PasswordOutlined color="action" />}
                    onChange={handleChange}
                    helperText={errors.password}
                    error={Boolean(errors.password)}
                  />

                  <SubField>
                    <Checkbox
                      name="rememberMe"
                      label={
                        languageLoading ? (
                          <Skeleton
                            variant="text"
                            width={200}
                            height={20}
                            sx={{ transform: 'none' }}
                            animation="wave"
                          />
                        ) : (
                          <>{t('label.label.login.remember_me')}</>
                        )
                      }
                      onChange={handleChange}
                      checked={values.rememberMe}
                    />

                    {languageLoading ? (
                      <Skeleton
                        variant="text"
                        width={150}
                        height={20}
                        sx={{ transform: 'none' }}
                        animation="wave"
                      />
                    ) : (
                      <Anchor
                        label={t('label.label.login.forgot_password')}
                        href={AppRouters.forgotPassword}
                        color={theme.palette.info.main}
                      />
                    )}
                  </SubField>
                </FormContainer>

                <LoadingButton
                  data-testid="login-button"
                  variant="contained"
                  size="large"
                  type="submit"
                  loading={loading}
                  sx={{
                    color: theme.palette.text.primary,
                    height: '44px',
                  }}
                >
                  {languageLoading ? (
                    <Skeleton
                      variant="text"
                      width={150}
                      height={20}
                      sx={{ transform: 'none' }}
                      animation="wave"
                    />
                  ) : (
                    <>{t('button.button.login')}</>
                  )}
                </LoadingButton>
              </FormContainer>
            </Form>
          )}
        </Formik>
      </AuthTemplate>

      <Alert
        open={alert.open}
        severity={alert.severity}
        message={alert.message}
        autoHideDuration={5000}
        onClose={handleCloseAlert}
      />
    </PublicRoute>
  );
}
